import html2pdf from "html2pdf.js";

export const fillTemplate = (html, data) => {
  let filled = html;
  Object.keys(data).forEach((key) => {
    const regex = new RegExp(`{{${key}}}`, "g");
    filled = filled.replace(regex, data[key] ?? "");
  });
  return filled;
};

export const loadTemplate = async (docType, previewData) => {
  // Map document type to template filename
  const templateMap = {
    "employment_certificate": "employment_certificate",
    "payslip": "payslip",
  };
  
  const templateName = templateMap[docType] || docType;
  const templatePath = `/templates/${templateName}.html`;
  
  console.log("📄 Loading template:", templatePath, "for docType:", docType);
  
  const res = await fetch(templatePath);
  
  if (!res.ok) {
    throw new Error(`Failed to load template: ${templatePath} (Status: ${res.status})`);
  }
  
  let html = await res.text();
  
  if (!html || html.trim().length === 0) {
    throw new Error(`Template file is empty: ${templatePath}`);
  }
  
  console.log("✅ Template loaded, HTML length:", html.length);

  // 1️⃣ Remove whole label <p> lines by label name (only for employment certificate)
  if (docType === "employment_certificate") {
    const removeLabels = [
      "Date of Birth",
      "Phone",
      "Status",
      "Achievements",
      "Remarks",
    ];

    removeLabels.forEach((label) => {
      const regex = new RegExp(
        `<p[^>]*>\\s*<strong>${label}:<\\/strong>.*?<\\/p>`,
        "gis"
      );
      html = html.replace(regex, "");
    });

    // 2️⃣ Replace placeholders directly (only for employment certificate)
    html = html
      .replace(/{{dob}}/gi, "")
      .replace(/{{status}}/gi, "")
      .replace(/{{achievements}}/gi, "")
      .replace(/{{remarks}}/gi, "")
      .replace(/{{workerPhone}}/gi, "");
  } else if (docType === "payslip") {
    // For payslip, only replace workerEmail as it's not in the payslip template
    html = html.replace(/{{workerEmail}}/gi, previewData.companyEmail || "");
  }

  // 3️⃣ Clean up any empty <p> tags left behind
  html = html.replace(/<p>\s*<\/p>/g, "");

  // 4️⃣ Fix malformed logo URL if needed
  if (previewData.companyLogo) {
    // Fix double path issue (handles /uploads/logos/http://, /uploads/logos/https://, etc.)
    if (previewData.companyLogo.startsWith("/uploads/logos/")) {
      const remainder = previewData.companyLogo.substring("/uploads/logos/".length);
      // Check if remainder starts with a protocol (http://, https://, ftp://, etc.)
      if (/^[a-z]+:\/\//i.test(remainder)) {
        console.log("🔧 Fixing double path in logo URL:", previewData.companyLogo.substring(0, 80));
        previewData.companyLogo = remainder;
      }
    }
    // Ensure absolute URL if it's a relative path
    if (previewData.companyLogo.startsWith("/") && !previewData.companyLogo.startsWith("http")) {
      // If it's base64, keep it as is
      if (!previewData.companyLogo.startsWith("data:")) {
        // For relative paths, you might need to prepend BASE_URL
        // But if it's already base64 from the hook, it should work
        console.log("📷 Logo URL:", previewData.companyLogo.substring(0, 50));
      }
    }

    // Validate logo is a proper data URL or valid URL
    if (!previewData.companyLogo.startsWith("data:") &&
        !previewData.companyLogo.startsWith("http") &&
        previewData.companyLogo.trim() !== "") {
      console.warn("⚠️ Company logo might not be in the expected format");
    }
  }

  // Log preview data before filling
  console.log("📋 Preview data keys:", Object.keys(previewData));
  console.log("📋 Preview data sample:", {
    companyName: previewData.companyName,
    workerName: previewData.workerName,
    workerId: previewData.workerId,
    hasLogo: !!previewData.companyLogo,
    logoLength: previewData.companyLogo?.length || 0,
  });
  
  const filledHtml = fillTemplate(html, previewData);
  
  // Log filled template preview
  console.log("✅ Template filled, final HTML length:", filledHtml.length);
  console.log("📋 Filled HTML preview (first 1000 chars):", filledHtml.substring(0, 1000));
  
  // Verify all placeholders were replaced
  const remainingPlaceholders = filledHtml.match(/{{[^}]+}}/g);
  if (remainingPlaceholders && remainingPlaceholders.length > 0) {
    console.warn("⚠️ Unreplaced placeholders found:", remainingPlaceholders);
    console.warn("⚠️ This might cause empty content in PDF");
  } else {
    console.log("✅ All placeholders replaced successfully");
  }
  
  // Check if filled HTML has actual content (not just empty tags)
  const textContent = filledHtml.replace(/<[^>]+>/g, "").trim();
  if (textContent.length < 50) {
    console.warn("⚠️ Filled HTML has very little text content:", textContent.length, "chars");
    console.warn("⚠️ This might result in an empty PDF");
  } else {
    console.log("✅ Filled HTML has substantial text content:", textContent.length, "chars");
  }
  
  return filledHtml;
};

export const generatePDF = async (
  filledHtml,
  filename = "document.pdf",
  returnBlob = false
) => {
  // Check if HTML has content
  if (!filledHtml || filledHtml.trim().length === 0) {
    throw new Error("HTML content is empty");
  }
  
  console.log("📋 HTML length:", filledHtml.length);
  console.log("📋 HTML preview:", filledHtml.substring(0, 200));
  
  // Extract body content and styles if full HTML document
  let bodyContent = filledHtml;
  let styles = "";
  
  const bodyMatch = filledHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const styleMatches = filledHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  
  if (bodyMatch) {
    bodyContent = bodyMatch[1];
    console.log("✅ Extracted body content, length:", bodyContent.length);
  } else {
    // If no body tag, use the entire HTML as content
    bodyContent = filledHtml;
    console.log("⚠️ No body tag found, using entire HTML as content");
  }
  
  if (styleMatches) {
    styles = styleMatches.map(match => {
      const content = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      return content ? content[1] : "";
    }).join("\n");
    console.log("✅ Extracted styles, length:", styles.length);
  }
  
  // Create container and append to body
  const container = document.createElement("div");
  container.id = "pdf-generation-container";
  
  // Add styles to document head if we extracted them
  let styleElement = null;
  if (styles) {
    styleElement = document.createElement("style");
    styleElement.id = "pdf-generation-styles";
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
  }
  
  // Set body content - preserve the full structure from template
  // Use the entire body content including the certificate-container div
  container.innerHTML = bodyContent;
  
  // Log what we're putting in the container
  console.log("📦 Container HTML length:", container.innerHTML.length);
  console.log("📦 Container first 1000 chars:", container.innerHTML.substring(0, 1000));
  
  // Verify the document container exists (supports both certificate and payslip)
  const docContainer = container.querySelector('.certificate-container, .payslip-container');
  if (!docContainer) {
    console.error("❌ Document container (certificate or payslip) not found in HTML!");
    console.error("📋 Available classes:", Array.from(container.querySelectorAll('[class]')).map(el => el.className));
  } else {
    console.log("✅ Document container found:", docContainer.className);
    console.log("📋 Document container content length:", docContainer.innerHTML.length);
    console.log("📋 Document container text:", docContainer.textContent?.substring(0, 200));
  }
  
  // Style container - position off-screen but fully visible for html2canvas
  // html2canvas needs the element to be in the DOM and visible
  // A4 size: 210mm x 297mm = 794px x 1123px at 96dpi
  container.style.position = "absolute";
  container.style.left = "-9999px"; // Off-screen but still in DOM
  container.style.top = "0";
  container.style.width = "794px"; // 210mm in pixels at 96dpi
  container.style.minWidth = "794px";
  container.style.maxWidth = "794px";
  container.style.backgroundColor = "#fff";
  container.style.visibility = "visible";
  container.style.display = "block";
  container.style.opacity = "1"; // Fully visible for html2canvas
  container.style.pointerEvents = "none";
  container.style.zIndex = "-1"; // Behind everything
  container.style.overflow = "visible";
  container.style.height = "auto";
  container.style.minHeight = "1123px"; // 297mm - ensure minimum A4 height
  container.style.transform = "translateZ(0)"; // Force GPU acceleration
  container.style.boxSizing = "border-box";
  
  document.body.appendChild(container);
  
  // Force layout recalculation multiple times
  void container.offsetHeight;
  void container.scrollHeight;
  void container.offsetWidth;
  void container.scrollWidth;
  
  // Wait for initial render
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Force multiple reflows to ensure content is rendered
  void container.offsetHeight;
  void container.scrollHeight;
  void container.offsetWidth;
  void container.scrollWidth;
  
  // Wait for images (including base64 logos)
  const images = container.querySelectorAll("img");
  console.log("🖼️ Found images:", images.length);
  
  // Ensure images with base64 data URLs work correctly
  images.forEach((img, idx) => {
    if (img.src && img.src.startsWith("data:")) {
      // Base64 images should work, but ensure they're loaded
      console.log(`🖼️ Image ${idx + 1} is base64, length:`, img.src.length);
      img.onerror = () => {
        console.warn("⚠️ Base64 image failed to load:", img.src.substring(0, 50));
      };
    }
  });
  
  const imagePromises = Array.from(images).map((img, index) => {
    // Check if image is already loaded (for base64 images)
    if (img.complete && img.naturalHeight !== 0) {
      console.log(`✅ Image ${index + 1} already loaded (${img.naturalWidth}x${img.naturalHeight})`);
      return Promise.resolve();
    }
    
    // For base64 images, they should load immediately
    if (img.src && img.src.startsWith("data:")) {
      return new Promise((resolve) => {
        // Force image to load by setting src again
        const originalSrc = img.src;
        img.src = ""; // Clear first
        setTimeout(() => {
          img.src = originalSrc; // Set again to trigger load
          
          if (img.complete) {
            console.log(`✅ Base64 image ${index + 1} loaded`);
            resolve();
          } else {
            img.onload = () => {
              console.log(`✅ Base64 image ${index + 1} loaded via onload`);
              resolve();
            };
            img.onerror = () => {
              console.warn(`⚠️ Base64 image ${index + 1} failed to load`);
              resolve(); // Continue even if image fails
            };
            // Base64 should load quickly
            setTimeout(resolve, 1000);
          }
        }, 100);
      });
    }
    
    // For regular images
    return new Promise((resolve) => {
      img.onload = () => {
        console.log(`✅ Image ${index + 1} loaded`);
        resolve();
      };
      img.onerror = () => {
        console.warn(`⚠️ Image ${index + 1} failed to load:`, img.src?.substring(0, 50));
        resolve(); // Continue even if image fails
      };
      setTimeout(resolve, 5000); // Max 5 second wait for regular images
    });
  });
  
  await Promise.all(imagePromises);
  console.log("✅ All images processed");
  
  // Additional wait for rendering to complete
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Force final reflow
  void container.offsetHeight;
  void container.scrollHeight;
  
  // Log container state before PDF generation
  const containerState = {
    hasContent: container.innerHTML.length > 0,
    width: container.offsetWidth,
    height: container.offsetHeight,
    scrollWidth: container.scrollWidth,
    scrollHeight: container.scrollHeight,
    children: container.children.length,
    innerHTMLLength: container.innerHTML.length,
    computedStyle: window.getComputedStyle(container).display,
    textContent: container.textContent?.substring(0, 200) || "",
    hasDocContainer: container.querySelector('.certificate-container, .payslip-container') !== null,
  };
  
  console.log("📄 Container ready for PDF:", containerState);
  console.log("📄 Container HTML preview:", container.innerHTML.substring(0, 1000));

  // Validate container has content
  if (!containerState.hasContent || containerState.innerHTMLLength === 0) {
    throw new Error("Container has no content. HTML template may be empty or failed to load.");
  }
  
  // Check if document container exists
  if (!containerState.hasDocContainer) {
    console.warn("⚠️ Document container (certificate or payslip) not found in HTML. This might cause rendering issues.");
  }

  // Get actual dimensions - use scroll dimensions for accurate size
  const containerWidth = Math.max(container.scrollWidth, container.offsetWidth, 794);
  const containerHeight = Math.max(container.scrollHeight, container.offsetHeight, 1123);
  
  console.log("📐 Container dimensions:", {
    width: containerWidth,
    height: containerHeight,
    offsetWidth: container.offsetWidth,
    offsetHeight: container.offsetHeight,
    scrollWidth: container.scrollWidth,
    scrollHeight: container.scrollHeight,
  });

  // Verify content is actually visible
  if (container.offsetHeight === 0 || container.scrollHeight === 0) {
    console.warn("⚠️ Container has zero height! Trying to fix...");
    container.style.height = "auto";
    container.style.minHeight = "1123px";
    await new Promise(resolve => setTimeout(resolve, 500));
    void container.offsetHeight;
    
    // Check again after fix
    if (container.offsetHeight === 0 || container.scrollHeight === 0) {
      throw new Error("Container still has zero height after fix. Content may not be rendering properly.");
    }
  }

  // Make container fully visible for html2canvas (it needs to see the content)
  // But keep it off-screen so user doesn't see it
  container.style.left = "-9999px";
  container.style.opacity = "1"; // Full opacity for html2canvas
  container.style.visibility = "visible";
  
  // Force one more reflow after making it visible
  void container.offsetHeight;
  await new Promise(resolve => setTimeout(resolve, 300));

  const opt = {
    margin: [0, 0, 0, 0], // No margins on all sides
    filename,
    html2canvas: { 
      scale: 2,
      useCORS: true,
      logging: true, // Enable logging to debug
      allowTaint: true,
      backgroundColor: "#ffffff",
      width: containerWidth,
      height: containerHeight,
      windowWidth: containerWidth,
      windowHeight: containerHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      removeContainer: false,
      onclone: (clonedDoc) => {
        // Ensure cloned document has proper styles and is visible
        const clonedContainer = clonedDoc.getElementById('pdf-generation-container');
        if (clonedContainer) {
          clonedContainer.style.opacity = "1";
          clonedContainer.style.visibility = "visible";
          clonedContainer.style.position = "relative";
          clonedContainer.style.left = "0";
          clonedContainer.style.top = "0";
          clonedContainer.style.width = "794px";
          clonedContainer.style.backgroundColor = "#fff";
          clonedContainer.style.display = "block";
          
          // Also ensure the document container is visible (supports both certificate and payslip)
          const docContainer = clonedContainer.querySelector('.certificate-container, .payslip-container');
          if (docContainer) {
            docContainer.style.opacity = "1";
            docContainer.style.visibility = "visible";
            docContainer.style.width = "210mm";
            docContainer.style.minHeight = "297mm";
            docContainer.style.backgroundColor = "#fff";

            console.log("✅ Cloned document container found and styled:", docContainer.className);
            console.log("📋 Cloned container text:", docContainer.textContent?.substring(0, 200));
          } else {
            console.warn("⚠️ Document container (certificate or payslip) not found in cloned document");
          }
        } else {
          console.warn("⚠️ pdf-generation-container not found in cloned document");
        }
        return clonedDoc;
      },
    },
    jsPDF: { 
      unit: "mm", 
      format: "a4", 
      orientation: "portrait" 
    },
    pagebreak: { mode: ["avoid-all", "css", "legacy"] },
  };

  // Try to get the document container directly for better rendering
  // docContainer was already declared above, so reuse it
  const targetElement = docContainer || container;

  console.log("🎯 Using element for PDF:", {
    isDocContainer: docContainer !== null,
    elementType: targetElement.tagName,
    elementClass: targetElement.className,
    hasContent: targetElement.innerHTML.length > 0,
    textContent: targetElement.textContent?.substring(0, 200),
    offsetWidth: targetElement.offsetWidth,
    offsetHeight: targetElement.offsetHeight,
  });

  // Final validation - ensure element has content
  if (!targetElement || targetElement.innerHTML.length === 0) {
    throw new Error("Target element has no content. Cannot generate PDF.");
  }

  // Wait one more time to ensure everything is rendered
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Force final reflow
  void targetElement.offsetHeight;
  void targetElement.scrollHeight;

  let blob = null;

  try {
    if (returnBlob) {
      console.log("🔄 Generating PDF blob from element...");
      
      // Method 1: Try outputPdf("blob") - returns Promise<Blob>
      try {
        const pdfBlob = await html2pdf()
          .set(opt)
          .from(targetElement)
          .outputPdf("blob");
        
        console.log("📦 PDF output type:", typeof pdfBlob, pdfBlob instanceof Blob);
        
        if (pdfBlob instanceof Blob) {
          blob = pdfBlob;
        } else if (pdfBlob) {
          // If it's not a Blob, try to convert it
          blob = new Blob([pdfBlob], { type: "application/pdf" });
        } else {
          throw new Error("outputPdf('blob') returned null or undefined");
        }
      } catch (blobError) {
        console.warn("⚠️ outputPdf('blob') failed, trying alternative method:", blobError);
        
        // Method 2: Use outputPdf() to get arraybuffer, then convert to blob
        try {
          const pdfArrayBuffer = await html2pdf()
            .set(opt)
            .from(targetElement)
            .outputPdf("arraybuffer");
          
          if (pdfArrayBuffer && pdfArrayBuffer.byteLength > 0) {
            blob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
            console.log("✅ PDF created via arraybuffer method, size:", blob.size, "bytes");
          } else {
            throw new Error("ArrayBuffer is empty");
          }
        } catch (arrayBufferError) {
          console.error("❌ ArrayBuffer method also failed:", arrayBufferError);
          throw new Error("Both blob and arraybuffer methods failed: " + arrayBufferError.message);
        }
      }
      
      // Final validation
      if (!blob || blob.size === 0) {
        throw new Error("Generated PDF blob is empty. The PDF has no content. Container may not have rendered properly.");
      }
      
      console.log("✅ PDF blob created successfully, size:", blob.size, "bytes");
      console.log("✅ PDF blob type:", blob.type);
    } else {
      await html2pdf()
        .set(opt)
        .from(targetElement)
        .save();
    }
  } catch (error) {
    console.error("❌ Error generating PDF:", error);
    // Log container HTML for debugging
    console.log("📋 Container HTML (first 1000 chars):", container.innerHTML.substring(0, 1000));
    console.log("📋 Container text content (first 200 chars):", container.textContent?.substring(0, 200));
    throw error;
  } finally {
    // Clean up - remove container and styles
    if (container && container.parentNode) {
      document.body.removeChild(container);
    }
    if (styleElement && styleElement.parentNode) {
      document.head.removeChild(styleElement);
    }
  }

  return blob;
};
